/**
 * FinancialServicesDashboardPage — `/financial-services`.
 *
 * Operator-side overview of NIPOST Pay (wallets, transfers, bills, POS).
 * Slice 9: wired to real `GET /v1/financial-services/dashboard` endpoint.
 */
import { type JSX } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowRightLeft,
  CreditCard,
  Receipt,
  ShieldAlert,
  Wallet as WalletIcon,
} from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { getFinancialDashboard } from '../../lib/api/financial-services';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';
import { KPICard } from '../mail/shared/MailUI';

const NAV_LINKS: readonly { to: string; label: string }[] = [
  { to: '/financial-services/pos',           label: 'POS transactions' },
  { to: '/financial-services/transfers',     label: 'Money transfers' },
  { to: '/financial-services/bill-payments', label: 'Bill payments' },
  { to: '/financial-services/fraud',         label: 'Fraud alerts' },
];

export function FinancialServicesDashboardPage(): JSX.Element {
  const kpis = useQuery({
    queryKey: ['financial-services', 'dashboard'],
    queryFn: () => getFinancialDashboard(),
    retry: false,
  });

  const denied =
    kpis.isError && kpis.error instanceof ApiError &&
    (kpis.error.status === 401 || kpis.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Financial Services"
        description="Operator-side overview of NIPOST Pay — wallets, transfers, bills, POS."
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className="px-3 py-1.5 text-sm font-medium rounded-lg border border-border hover:bg-nipost-green-light hover:text-nipost-green transition-colors"
              >
                {l.label}
              </Link>
            ))}
          </div>
        }
      />

      {denied ? (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Financials/readDashboard</code> required.
        </DeferredNotice>
      ) : kpis.isError ? (
        <DeferredNotice title="Service temporarily unavailable">
          {(kpis.error as Error).message}
        </DeferredNotice>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KPICard
          title="Active wallets"
          value={
            kpis.isPending
              ? '…'
              : denied
                ? '—'
                : (kpis.data?.activeWallets ?? 0).toLocaleString('en-US')
          }
          hint="Wallets in active use"
          icon={WalletIcon}
        />
        <KPICard
          title="Transactions today"
          value={
            kpis.isPending
              ? '…'
              : denied
                ? '—'
                : (kpis.data?.todayTransactionCount ?? 0).toLocaleString('en-US')
          }
          hint="Wallet transactions captured today"
          icon={ArrowRightLeft}
        />
        <KPICard
          title="Flagged alerts"
          value={
            kpis.isPending
              ? '…'
              : denied
                ? '—'
                : (kpis.data?.flaggedAlertsCount ?? 0).toLocaleString('en-US')
          }
          hint="Open fraud alerts awaiting review"
          icon={ShieldAlert}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Quick navigation</h3>
          <div className="grid grid-cols-2 gap-3">
            <Link
              to="/financial-services/bill-payments"
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-nipost-green-light hover:border-nipost-green transition-colors"
            >
              <Receipt className="w-5 h-5 text-nipost-green" />
              <div>
                <p className="font-medium text-sm text-gray-900">Bill payments</p>
                <p className="text-xs text-muted">View payment ledger</p>
              </div>
            </Link>
            <Link
              to="/financial-services/transfers"
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-nipost-green-light hover:border-nipost-green transition-colors"
            >
              <ArrowRightLeft className="w-5 h-5 text-nipost-green" />
              <div>
                <p className="font-medium text-sm text-gray-900">Money transfers</p>
                <p className="text-xs text-muted">P2P transfer ledger</p>
              </div>
            </Link>
            <Link
              to="/financial-services/pos"
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-nipost-green-light hover:border-nipost-green transition-colors"
            >
              <CreditCard className="w-5 h-5 text-nipost-green" />
              <div>
                <p className="font-medium text-sm text-gray-900">POS transactions</p>
                <p className="text-xs text-muted">Terminal captures</p>
              </div>
            </Link>
            <Link
              to="/financial-services/fraud"
              className="flex items-center gap-3 p-4 rounded-lg border border-border hover:bg-nipost-green-light hover:border-nipost-green transition-colors"
            >
              <ShieldAlert className="w-5 h-5 text-nipost-green" />
              <div>
                <p className="font-medium text-sm text-gray-900">Fraud alerts</p>
                <p className="text-xs text-muted">Review alert queue</p>
              </div>
            </Link>
          </div>
        </div>
        <div className="bg-white border border-border rounded-xl p-5">
          <h3 className="font-semibold text-gray-900 mb-3">Fraud queue</h3>
          {kpis.data ? (
            kpis.data.flaggedAlertsCount > 0 ? (
              <div className="space-y-2">
                <p className="text-sm font-semibold text-amber-700">
                  {kpis.data.flaggedAlertsCount} alert{kpis.data.flaggedAlertsCount !== 1 ? 's' : ''} need attention
                </p>
                <Link
                  to="/financial-services/fraud"
                  className="inline-flex items-center gap-1 text-sm font-semibold text-nipost-green hover:underline"
                >
                  Open fraud alerts →
                </Link>
              </div>
            ) : (
              <p className="text-sm text-muted">No alerts pending review.</p>
            )
          ) : (
            <p className="text-sm text-muted">
              {kpis.isPending ? 'Loading…' : '—'}
            </p>
          )}
        </div>
      </div>

      <div className="bg-white border border-border rounded-xl p-5">
        <div className="flex items-center gap-3 mb-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h3 className="font-semibold text-gray-900">CBN compliance posture</h3>
        </div>
        <p className="text-sm text-muted">
          Per-tier limits are enforced server-side at the WalletTransaction
          guard (<code className="font-mono">Wallet.kycTier</code> + the
          tier-limit policy). Exact limits per tier surface on the wallet
          detail screen.
        </p>
      </div>
    </div>
  );
}
